import { Dispatch, SetStateAction, useCallback, useMemo } from "react"
import {
  Block,
  Divider,
  GasFee,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  selectPartialRepayStrategy,
  selectRepayStrategy,
  useDappSelector,
} from "#/redux-state"
import CurrencySymbol from "#/shared/components/CurrencySymbol"
import {
  ENABLE_BORROW_LIQUIDATIONS_PRICES,
  IS_MEZO_MAINNET,
} from "#/shared/constants"
import {
  useChangeInLiquidationPriceOnDebtChange,
  useCollateralPrice,
  useRepayBorrowAmount,
  useRepayCollateralizationRatio,
  useUpdatedCollateralBasedOnRatio,
} from "#/shared/hooks"
import { BorrowPartialRepayStrategy, BorrowRepayStrategy } from "#/shared/types"
import { getCurrencySymbol, separateThousandsWithComma } from "#/shared/utils"
import { ComponentStepProps } from "#/ui/Modal"

import {
  BorrowProgress,
  BorrowReviewBox,
  BorrowReviewListItem,
  BorrowTerms,
} from "../components"

type RepayReviewProps = ComponentStepProps & {
  handleRepay: () => Promise<void>
  setHasAcceptedTerms: Dispatch<SetStateAction<boolean>>
  hasAcceptedTerms: boolean
}
export default function RepayReview({
  onClickContinue,
  handleRepay,
  hasAcceptedTerms,
  setHasAcceptedTerms,
}: RepayReviewProps) {
  const [css, theme] = useStyletron()

  const repaymentStrategy = useDappSelector(selectRepayStrategy)
  const partialRepaymentStrategy = useDappSelector(selectPartialRepayStrategy)

  const isWithdrawingCollateral =
    partialRepaymentStrategy === BorrowPartialRepayStrategy.WITHDRAW_COLLATERAL

  const isTotalRepayment = repaymentStrategy === BorrowRepayStrategy.TOTAL

  const {
    principalRepayAmount,
    repayLoanAmount,
    prevDebtAmount,
    debtLeftToRepay,
    debtLeftToRepayLabel,
    interestAmountLabel,
    principalRepayAmountLabel,
  } = useRepayBorrowAmount()

  const repayLoanAmountLabel = useMemo(
    () => separateThousandsWithComma(repayLoanAmount),
    [repayLoanAmount],
  )

  const {
    collateralizationRatioChangeLabel,
    prevCollateralizationRatioLabel,
    newCollateralizationRatioLabel,
  } = useRepayCollateralizationRatio()

  const {
    additionalCollateralAmount,
    additionalCollateralAmountLabel,
    previousCollateralAmountLabel,
    updatedCollateralAmountLabel,
  } = useUpdatedCollateralBasedOnRatio(
    isWithdrawingCollateral ? -principalRepayAmount : 0n,
  )

  const {
    prevLiquidationPriceLabel,
    newLiquidationPriceLabel,
    changeInLiquidationPriceLabelWithUSD,
  } = useChangeInLiquidationPriceOnDebtChange(prevDebtAmount, debtLeftToRepay)

  const { collateralPriceLabelWithUSD } = useCollateralPrice(
    additionalCollateralAmount,
  )

  const onSubmit = useCallback(() => {
    handleRepay()
    onClickContinue()
  }, [handleRepay, onClickContinue])

  return (
    <>
      <BorrowProgress step={2} stepsCount={4} />
      <Block
        className={css({
          position: "relative",
          padding: theme.sizing.scale800,
          paddingTop: 0,
          minHeight: "712px",
          paddingBottom: "190px",
          overflow: "hidden",
        })}
      >
        <Block
          className={css({
            flexDirection: "column",
            display: "flex",
            gap: theme.sizing.scale800,
            height: "100%",
            paddingBottom: theme.sizing.scale800,
            overflow: "scroll",
            scrollbarWidth: "none",
          })}
        >
          {!isTotalRepayment && (
            <BorrowReviewBox>
              <BorrowReviewListItem
                header="Collateral"
                endHeader={`${updatedCollateralAmountLabel} ${getCurrencySymbol(
                  "mBTC",
                )}`}
                description={
                  isWithdrawingCollateral
                    ? `${additionalCollateralAmountLabel} ${getCurrencySymbol(
                        "mBTC",
                      )}`
                    : undefined
                }
                endDescription={
                  isWithdrawingCollateral
                    ? `from ${previousCollateralAmountLabel} ${getCurrencySymbol(
                        "mBTC",
                      )}`
                    : undefined
                }
                hasBorder
              />
              <BorrowReviewListItem
                header="Collateralization ratio"
                endHeader={`${
                  !isWithdrawingCollateral
                    ? newCollateralizationRatioLabel
                    : prevCollateralizationRatioLabel
                }%`}
                description={
                  !isWithdrawingCollateral
                    ? `+${collateralizationRatioChangeLabel}%`
                    : undefined
                }
                endDescription={
                  !isWithdrawingCollateral
                    ? `from ${prevCollateralizationRatioLabel}%`
                    : undefined
                }
              />
            </BorrowReviewBox>
          )}

          {ENABLE_BORROW_LIQUIDATIONS_PRICES && IS_MEZO_MAINNET && (
            <BorrowReviewBox>
              <BorrowReviewListItem
                header="Liquidation price"
                endHeader={`$${
                  isWithdrawingCollateral
                    ? prevLiquidationPriceLabel
                    : newLiquidationPriceLabel
                }`}
                description={
                  !isWithdrawingCollateral
                    ? changeInLiquidationPriceLabelWithUSD
                    : undefined
                }
                endDescription={
                  !isWithdrawingCollateral
                    ? `from $${prevLiquidationPriceLabel}`
                    : undefined
                }
              />
            </BorrowReviewBox>
          )}

          {(isTotalRepayment || isWithdrawingCollateral) && (
            <Block
              className={css({
                borderRadius: theme.borders.radius400,
                border: "1px solid",
                borderColor: theme.colors.borderOpaque,
              })}
            >
              <BorrowReviewListItem
                header="Collateral to withdraw"
                endHeader={`${additionalCollateralAmountLabel.replace(
                  "-",
                  "",
                )} mBTC`}
                endDescription={
                  ENABLE_BORROW_LIQUIDATIONS_PRICES && IS_MEZO_MAINNET
                    ? `${collateralPriceLabelWithUSD}`
                    : undefined
                }
              />
              <Divider $size="cell" />
              <Block
                className={css({
                  display: "flex",
                  justifyContent: "space-between",
                  padding: theme.sizing.scale600,
                  paddingTop: theme.sizing.scale300,
                })}
              >
                <ParagraphSmall color={theme.colors.contentSecondary}>
                  Transaction gas fee
                </ParagraphSmall>
                <Block display="flex" alignItems="center">
                  <GasFee
                    color={theme.colors.contentTertiary}
                    size="14px"
                    className={css({
                      marginBottom: "1px",
                    })}
                  />
                  <ParagraphSmall marginLeft={theme.sizing.scale100}>
                    {"<0.000001"} <CurrencySymbol token="mBTC" />
                  </ParagraphSmall>
                </Block>
              </Block>
            </Block>
          )}

          <BorrowReviewBox backgroundColor={theme.colors.green50}>
            <BorrowReviewListItem
              hasLightHeader
              header="Accured interest"
              description="Deducted before reducing debt"
              endHeader={`${interestAmountLabel} MUSD`}
              hasBorder
            />
            <BorrowReviewListItem
              hasLightHeader
              header="Debt reduction"
              endHeader={`-${principalRepayAmountLabel} MUSD`}
            />
            <BorrowReviewListItem
              header="Remaining debt"
              endHeader={
                isTotalRepayment
                  ? // On total repayment the interest will be deducted from the total amount
                    "0 MUSD"
                  : `${debtLeftToRepayLabel} MUSD`
              }
              margin={`${theme.sizing.scale800} 0 0`}
              hasBorder
            />
            <BorrowReviewListItem
              header="Total amount to repay"
              endHeader={`${repayLoanAmountLabel} MUSD`}
            />
          </BorrowReviewBox>
        </Block>

        <BorrowTerms
          dataAttr="repay_loan-step_5"
          hasAcceptedTerms={hasAcceptedTerms}
          setHasAcceptedTerms={setHasAcceptedTerms}
          onSubmit={onSubmit}
          label={`Repay ${repayLoanAmountLabel} MUSD`}
        />
      </Block>
    </>
  )
}
